import { z } from 'zod';

import { StringBooleanSchema } from './util';

/**
 * @see {@link PublicStaticEnvironmentVars} for an explanation of what constitutes a public static
 * environment variable and what variables are expected to be included in this set.
 */
export const PublicStaticClientEnvironmentSchema = z.object({
  idleUserWatcherEnabled: StringBooleanSchema.optional(),
});

/**
 * Represents the environment variables that are prefixed with NEXT_PUBLIC_ and are safe to access
 * both client and server side but are also not dynamic and do not need to rely on usage of the
 * dynamic /api/client-environment route.
 *
 * Environment variables that are allowed to be present here must satisfy all of the following:
 *
 * 1. Static: The variable's value should be allowed to be determined at build time without changing
 *    at runtime. In other words, since our "build-then-promote" strategy, which will not include
 *    all environment variables in the build but injects them later on - will not reflect updated
 *    values of these variables after the built image is deployed to a given environment, they
 *    must be capable of working statically based on their values at build time.
 * 2. Public: The variable should be safe to expose publicly on the client side, meaning it should
 *    not contain sensitive information such as API keys, secrets, or personally identifiable
 *    information.  All variables must be prefixed with NEXT_PUBLIC_ and any variable that is not
 *    prefixed with NEXT_PUBLIC_ will be rejected by the schema validation.
 */
export type PublicStaticEnvironmentVars = z.infer<typeof PublicStaticClientEnvironmentSchema>;

/**
 * Retrieves the public, static environment variables for the application after validating them
 * against the defined schema.
 *
 * @see {@link PublicStaticEnvironmentVars} for an explanation of what constitutes a public static
 * environment variable and what variables are expected to be included in this set.
 *
 * @example
 * const idleUserWatcherEnabled = getPublicStaticEnvironment().idleUserWatcherEnabled;
 *
 * @throws {z.ZodError} If any of the environment variables are invalid.
 *
 * @returns {PublicStaticEnvironmentVars}
 *   The type-safe, validated environment variables that are safe to access on client and server
 *   that are static and do not change between build and deployment cycles.
 */
export const getPublicStaticEnvironment = (): PublicStaticEnvironmentVars => {
  const parsed = PublicStaticClientEnvironmentSchema.safeParse({
    idleUserWatcherEnabled: process.env.NEXT_PUBLIC_IDLE_USER_WATCHER_ENABLED,
  });
  if (parsed.error) {
    /* eslint-disable-next-line no-console -- The logger depends on the environment module so
       the environment module must avoid logger usage. */
    console.error(parsed.error, 'There was an error parsing the environment variables!');
    throw parsed.error;
  }
  return parsed.data;
};
